/**
 * Module: Widget navigation context
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R13, BROWSE-V1-R20
 *
 * Tiny standalone module holding the React context that lets the
 * retrieval surfaces' ``BrowseLink`` ask the host wrapper
 * (``SurfaceHost``) to swap to the Browse viewer in place. Kept
 * separate from both ``surface-host.tsx`` (the provider) and
 * ``browse-link.tsx`` (the consumer) so the two don't form an
 * import cycle.
 */

import { createContext, useContext } from 'react';

/**
 * Navigation API exposed to descendants. ``openBrowse`` swaps the
 * mounted surface to the Browse viewer in place and pushes a
 * browser history entry so the Back button returns to the prior
 * surface (with its React state intact).
 */
export interface WidgetNavigationValue {
  openBrowse: (guidelineId: number, sectionId: number | null) => void;
}

export const WidgetNavigationContext =
  createContext<WidgetNavigationValue | null>(null);

/**
 * Read the widget navigation API. Returns ``null`` when the
 * component is rendered outside a ``SurfaceHost`` (e.g. an isolated
 * unit test that doesn't exercise navigation), so callers must
 * null-check.
 */
export function useWidgetNavigation(): WidgetNavigationValue | null {
  return useContext(WidgetNavigationContext);
}
